// Payments/WithdrawalHistoryTable.js

import React, { useState, useEffect } from 'react';
import axiosInstance from "../../../utils/axiosInstance";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, CircularProgress, Alert, Box, TextField, IconButton, Tooltip,
  Stack, Chip, Typography, useTheme, alpha
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';
import PendingIcon from '@mui/icons-material/Pending';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const FONT = "'Plus Jakarta Sans', sans-serif";

const WithdrawalHistoryTable = ({ perPage = 10, showPagination = true, refreshKey }) => {
  const theme = useTheme();
  const [withdrawals, setWithdrawals] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(perPage);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [checkingId, setCheckingId] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    const params = {
      page: page + 1,
      per_page: rowsPerPage,
      start_date: startDate || null,
      end_date: endDate || null,
    };
    axiosInstance.get(`/payments/withdrawals`, { params })
      .then(res => {
        setWithdrawals(res.data.data);
        setTotalRows(res.data.total);
      })
      .catch(err => setError('Failed to fetch withdrawal history.'))
      .finally(() => setIsLoading(false));
  }, [page, rowsPerPage, startDate, endDate, refreshKey]);

  const handleCheckStatus = async (transactionId) => {
    setCheckingId(transactionId);
    try {
      const res = await axiosInstance.post(`/payments/withdrawals/${transactionId}/check-status`, {});
      const newStatus = res.data.new_status;

      setWithdrawals(currentWithdrawals =>
        currentWithdrawals.map(w =>
          w.id === transactionId ? { ...w, status: newStatus } : w
        )
      );
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to check status.');
    } finally {
      setCheckingId(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'succeeded':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'pending':
        return <PendingIcon color="warning" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'succeeded':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  /* ---------- header cell shared style ---------- */
  const thSx = {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
    bgcolor: alpha(theme.palette.text.primary, 0.02),
    borderBottom: '2px solid ' + theme.palette.divider,
  };

  /* ---------- body cell shared style ---------- */
  const tdSx = {
    fontFamily: FONT,
    fontSize: '0.85rem',
    py: 1.5,
  };

  /* ---------- loading state ---------- */
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress size={32} thickness={4} />
      </Box>
    );
  }

  /* ---------- error state ---------- */
  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ fontFamily: FONT, borderRadius: 2, mx: 1, my: 2 }}
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* date filters */}
      {showPagination && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
            sx={{ '& .MuiInputBase-root': { fontFamily: FONT, fontSize: '0.85rem' } }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
            sx={{ '& .MuiInputBase-root': { fontFamily: FONT, fontSize: '0.85rem' } }}
          />
        </Stack>
      )}

      {/* table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={thSx}>#</TableCell>
              <TableCell sx={thSx}>Date</TableCell>
              <TableCell sx={thSx}>Amount</TableCell>
              <TableCell sx={thSx}>Gateway</TableCell>
              <TableCell sx={thSx}>Destination</TableCell>
              <TableCell sx={thSx}>Status</TableCell>
              <TableCell align="center" sx={thSx}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {withdrawals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ border: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 8,
                    }}
                  >
                    <ReceiptLongIcon
                      sx={{
                        fontSize: 48,
                        color: alpha(theme.palette.text.secondary, 0.3),
                        mb: 1.5,
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontSize: '0.9rem',
                        color: theme.palette.text.secondary,
                      }}
                    >
                      No withdrawal history found
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              withdrawals.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  {/* # */}
                  <TableCell sx={tdSx}>
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontSize: '0.8rem',
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {row.s_no}
                    </Typography>
                  </TableCell>

                  {/* Date */}
                  <TableCell sx={tdSx}>
                    <Typography
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.8rem',
                        fontFamily: FONT,
                      }}
                    >
                      {row.datetime}
                    </Typography>
                  </TableCell>

                  {/* Amount */}
                  <TableCell sx={tdSx}>
                    <Typography
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontWeight: 600,
                        fontFeatureSettings: '"tnum"',
                        fontSize: '0.85rem',
                      }}
                    >
                      {(row.gateway === 'crypto_manual' || row.gateway === 'coinbase')
                        ? `${row.crypto_amount ?? 0} ${row.crypto_token || 'USDC'}`
                        : `$${row.amount}`
                      }
                    </Typography>
                  </TableCell>

                  {/* Gateway */}
                  <TableCell sx={tdSx}>
                    <Chip
                      label={
                        row.gateway === 'stripe_connect' ? 'Stripe'
                        : row.gateway === 'paypal' ? 'PayPal'
                        : row.gateway === 'coinbase' ? 'Coinbase'
                        : row.gateway === 'crypto_manual' ? 'Crypto'
                        : row.gateway || '--'
                      }
                      size="small"
                      sx={{
                        fontFamily: FONT,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 22,
                        borderRadius: 1.5,
                      }}
                    />
                  </TableCell>

                  {/* Destination */}
                  <TableCell sx={tdSx}>
                    <Typography
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.75rem',
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {row.destination_wallet || row.destination_account_id || '--'}
                    </Typography>
                  </TableCell>

                  {/* Status */}
                  <TableCell sx={tdSx}>
                    <Chip
                      label={row.status}
                      size="small"
                      color={getStatusColor(row.status)}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 24,
                        borderRadius: 1.5,
                        fontFamily: FONT,
                        textTransform: 'capitalize',
                      }}
                    />
                  </TableCell>

                  {/* Action */}
                  <TableCell align="center" sx={tdSx}>
                    {row.status === 'pending' ? (
                      <Tooltip title="Check Status" arrow>
                        <IconButton
                          onClick={() => handleCheckStatus(row.id)}
                          disabled={checkingId === row.id}
                          size="small"
                          sx={{
                            border: '1px solid ' + alpha(theme.palette.primary.main, 0.4),
                            borderRadius: 1.5,
                            width: 32,
                            height: 32,
                            color: theme.palette.primary.main,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              borderColor: theme.palette.primary.main,
                            },
                          }}
                        >
                          {checkingId === row.id ? (
                            <CircularProgress size={16} thickness={4} />
                          ) : (
                            <SyncIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    ) : (
                      getStatusIcon(row.status)
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* pagination */}
      {showPagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            fontFamily: FONT,
            borderTop: '1px solid ' + theme.palette.divider,
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontFamily: FONT,
              fontSize: '0.8rem',
            },
          }}
        />
      )}
    </Box>
  );
};

export default WithdrawalHistoryTable;
